import { useCurrentRouteKey } from '#app/hooks/useCurrentRouteKey.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { GlobalModalContext } from './GlobalModalContext.js';
import {
  MODAL_ANALYTICS_NAMES,
  type MODAL_MAP,
} from './modalImportDefinitions.js';
import { type ModalArgs } from './types.js';
import {
  lazy,
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const modalNamesToNotCloseOnRouteChange: Array<keyof MODAL_MAP> = [
  // this modal needs to stay open on route changes, since it opens after a checkout that redirects to discover
  'GuestPaymentSuccessModal',
  // this modal needs to stay open on route changes, since it opens after completing onboarding that redirects to discover
  'JobCreationModal',
];

export const GlobalModalProvider = ({
  children,
}: {
  readonly children: ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeModalId, setActiveModalId] = useState<null | string>(null);
  const [activeModalName, setActiveModalName] = useState<
    keyof MODAL_MAP | null
  >(null);
  const [activeModalProps, setActiveModalProps] = useState<ModalArgs | null>(
    null,
  );
  const [urlParameters, setUrlParameters] = useURLParameters('*');
  const currentRouteKey = useCurrentRouteKey();
  const previousRouteKey = useRef<null | string>(currentRouteKey);

  const openModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setActiveModalName(null);
    setActiveModalProps(null);
    setActiveModalId(null);
    setIsOpen(false);
  }, []);

  const context = useMemo(() => {
    return {
      activeModalId,
      activeModalName,
      closeModal,
      isOpen,
      openModal,
      setActiveModalId,
      setActiveModalName,
      setActiveModalProps,
    };
  }, [
    activeModalId,
    activeModalName,
    closeModal,
    isOpen,
    openModal,
    setActiveModalId,
    setActiveModalProps,
  ]);

  useEffect(() => {
    if (
      urlParameters.modalName &&
      urlParameters.modalName in MODAL_ANALYTICS_NAMES
    ) {
      setActiveModalName(urlParameters.modalName as keyof MODAL_MAP);
      setActiveModalProps({});
      setActiveModalId(urlParameters.modalName);
      setIsOpen(true);
      setUrlParameters({ modalName: undefined });
    }
  }, [setUrlParameters, urlParameters.modalName]);

  // For modals using url parameters (ie modals opened post stripe checkout),
  // we need to close the modal when the route changes
  // Otherwise, they might stay open during page transitions
  useEffect(() => {
    if (previousRouteKey.current !== currentRouteKey) {
      if (
        activeModalName &&
        !urlParameters.modalName &&
        !modalNamesToNotCloseOnRouteChange.includes(activeModalName)
      ) {
        closeModal();
      }

      previousRouteKey.current = currentRouteKey;
    }
    // This should only ever run if there is a new route
    // Should not run on param changes etc
  }, [currentRouteKey]);

  const CurrentTemplate = useMemo(() => {
    if (activeModalName === null) {
      return null;
    }

    return lazy<MODAL_MAP[typeof activeModalName]>(async () => {
      return {
        default: (await import('./templates/' + activeModalName + '.tsx'))[
          activeModalName
        ],
      };
    });
  }, [activeModalName]);

  return (
    <GlobalModalContext.Provider value={context}>
      <>
        {activeModalName && CurrentTemplate ? (
          <Suspense>
            <CurrentTemplate
              closeModal={closeModal}
              isOpen={isOpen}
              key={`${activeModalName}-${activeModalId}`}
              modalName={MODAL_ANALYTICS_NAMES[activeModalName]}
              openModal={openModal}
              {...activeModalProps}
            />
          </Suspense>
        ) : null}
        {children}
      </>
    </GlobalModalContext.Provider>
  );
};
